import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { toast } from '../hooks/use-toast';

const AuthModal = ({ isOpen, onClose, mode, onModeChange }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: mode === 'login' ? t('toast.loginSuccess') : t('auth.createAccount'),
      description: mode === 'login' ? t('toast.loginSuccess') : t('auth.signingUp'),
    });
    onClose();
  };

  const handleSocialLogin = (provider) => {
    toast({
      title: `${provider} ${t('auth.login')}`,
      description: `${t('auth.loggingIn')} ${provider}…`,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] p-0">
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold">
              {mode === 'login' ? t('auth.login') : t('auth.signup')}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="px-6 pb-6">
          <h3 className="text-2xl font-semibold mb-6">
            {mode === 'login' ? t('auth.loginTitle') : t('auth.signupTitle')}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <Label htmlFor="name">{t('booking.fullName')}</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('booking.fullName')}
                  className="mt-1"
                  required
                />
              </div>
            )}

            <div>
              <Label htmlFor="email">{t('booking.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label htmlFor="password">{t('wifi.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder')}
                className="mt-1"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-rose-500 hover:bg-rose-600 text-white h-12 font-semibold transition-colors"
            >
              {mode === 'login' ? t('auth.login') : t('auth.signup')}
            </Button>
          </form>

          <div className="my-6">
            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm text-gray-500">
                {t('auth.or')}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => handleSocialLogin('Google')}
              variant="outline"
              className="w-full h-12 border-gray-900 hover:bg-gray-50 font-semibold"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {t('auth.continueWithGoogle')}
            </Button>
          </div>

          <div className="mt-6 text-center text-sm">
            <span className="text-gray-600">
              {mode === 'login' ? t('auth.noAccount') : t('auth.haveAccount')}
            </span>
            <button
              onClick={() => onModeChange(mode === 'login' ? 'signup' : 'login')}
              className="ml-2 font-semibold text-gray-900 underline hover:text-gray-700"
            >
              {mode === 'login' ? t('auth.signup') : t('auth.login')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
